/**
 * Header Custom JavaScript
 * NoTe Theme Header Component
 *
 * This file contains all JavaScript functionality for the header component
 */

(function () {
  'use strict';

  /**
   * Initialize header height CSS variable
   * Sets the --header-height custom property based on the actual header height
   */
  function initHeaderHeight() {
    const headerSection = document.querySelector('.shopify-section--header');
    if (headerSection) {
      const height = headerSection.clientHeight;
      document.documentElement.style.setProperty('--header-height', `${height.toFixed(2)}px`);
    }
  }

  /**
   * Update header height on window resize
   * Debounced to prevent excessive calculations
   */
  function setupResizeHandler() {
    let resizeTimeout;

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(initHeaderHeight, 150);
    });
  }

  /**
   * Calculate and adjust dropdown menu position to prevent overflow
   * Positions dropdown from right if it would overflow the viewport
   */
  function adjustDropdownPosition(dropdown, menu) {
    if (!dropdown || !menu) return;

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportPadding = 16; // Add some padding from viewport edges

    // Get dropdown item position
    const dropdownRect = dropdown.getBoundingClientRect();

    // Get menu dimensions - use offsetWidth/offsetHeight for more reliable measurement
    // even when menu is hidden
    const menuWidth = menu.offsetWidth || menu.getBoundingClientRect().width || 400;

    // Calculate position if menu were positioned at left: 0 (default)
    const leftPosition = dropdownRect.left;
    const rightEdgePosition = leftPosition + menuWidth;

    // Check if menu would overflow on the right
    const wouldOverflowRight = rightEdgePosition > (viewportWidth - viewportPadding);

    // Check if menu would overflow on the left
    const wouldOverflowLeft = leftPosition < viewportPadding;

    // Reset positioning first
    menu.style.left = '';
    menu.style.right = '';

    // Apply positioning based on overflow detection
    if (wouldOverflowRight && !wouldOverflowLeft) {
      // Position from right side of the dropdown item
      menu.style.left = 'auto';
      menu.style.right = '0';
    } else if (wouldOverflowLeft && !wouldOverflowRight) {
      // Position from left edge of viewport with padding
      menu.style.left = '0';
      menu.style.right = 'auto';
      // Adjust to account for viewport padding
      const adjustment = viewportPadding - dropdownRect.left;
      if (adjustment > 0) {
        menu.style.left = adjustment + 'px';
      }
    } else if (wouldOverflowRight && wouldOverflowLeft) {
      // Both sides overflow - position from right edge of viewport
      menu.style.left = 'auto';
      menu.style.right = viewportPadding + 'px';
    } else {
      // No overflow - use default left: 0
      menu.style.left = '0';
      menu.style.right = 'auto';
    }
  }

  /**
   * Initialize dropdown positions on page load
   * Prevents horizontal scrollbar by calculating positions before menus are shown
   */
  function initializeDropdownPositions() {
    const dropdowns = document.querySelectorAll('.header-note__dropdown');

    dropdowns.forEach(function (dropdown) {
      const menu = dropdown.querySelector('.header-note__dropdown-menu');
      if (!menu) return;

      // Get computed styles
      const computedStyle = window.getComputedStyle(menu);

      // Temporarily make menu measurable (invisible but rendered)
      // We need to override CSS to make it measurable, but we'll remove these styles after
      const tempStyles = {
        visibility: 'hidden',
        opacity: '0',
        display: 'block',
        transform: 'translateY(0)',
        pointerEvents: 'none'
      };

      // Store original inline styles (if any)
      const originalInlineStyles = {
        visibility: menu.style.visibility,
        opacity: menu.style.opacity,
        display: menu.style.display,
        transform: menu.style.transform,
        pointerEvents: menu.style.pointerEvents
      };

      // Apply temporary styles for measurement
      Object.keys(tempStyles).forEach(function (key) {
        menu.style[key] = tempStyles[key];
      });

      // Ensure it's positioned
      if (computedStyle.position === 'static') {
        menu.style.position = 'absolute';
      }

      // Use requestAnimationFrame to ensure layout is calculated
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          // Now measure and adjust position (this sets left/right styles)
          adjustDropdownPosition(dropdown, menu);

          // Remove all temporary inline styles - let CSS handle visibility/opacity/transform
          // Only keep positioning styles (left/right) that we set in adjustDropdownPosition
          Object.keys(tempStyles).forEach(function (key) {
            // Remove inline style to let CSS :hover rules work
            if (originalInlineStyles[key] === '') {
              menu.style.removeProperty(key);
            } else {
              menu.style[key] = originalInlineStyles[key];
            }
          });
        });
      });
    });
  }

  /**
   * Setup dropdown hover delay to prevent premature closing
   * Allows users to move mouse from parent to menu without dropdown closing
   */
  function setupDropdownHoverDelay() {
    const dropdowns = document.querySelectorAll('.header-note__dropdown');
    const HIDE_DELAY = 100; // Delay in milliseconds before hiding dropdown (0.1s)

    dropdowns.forEach(function (dropdown) {
      const menu = dropdown.querySelector('.header-note__dropdown-menu');
      if (!menu) return;

      let hideTimeout = null;
      let isMouseOverDropdown = false;
      let isMouseOverMenu = false;

      // Function to check if mouse is over dropdown or menu
      const isHovering = function () {
        // Also check using matches for reliability
        return isMouseOverDropdown || isMouseOverMenu ||
          dropdown.matches(':hover') ||
          menu.matches(':hover');
      };

      // Function to show dropdown
      const showDropdown = function () {
        // Clear any pending hide timeout
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          hideTimeout = null;
        }

        // Show the menu using inline styles (overrides CSS)
        // Inline styles have higher specificity than CSS rules
        menu.style.opacity = '1';
        menu.style.visibility = 'visible';
        menu.style.transform = 'translateY(0)';
        menu.style.pointerEvents = 'auto';
      };

      // Function to hide dropdown with delay
      const hideDropdown = function () {
        // Clear any existing timeout
        if (hideTimeout) {
          clearTimeout(hideTimeout);
        }

        // Set timeout to hide after delay
        hideTimeout = setTimeout(function () {
          // Double-check if still not hovering before hiding
          const stillHovering = isHovering();
          if (!stillHovering) {
            // Remove inline styles to let CSS handle it
            menu.style.opacity = '';
            menu.style.visibility = '';
            menu.style.transform = '';
            menu.style.pointerEvents = '';
            hideTimeout = null;
          } else {
            // Still hovering, clear timeout and keep showing
            hideTimeout = null;
          }
        }, HIDE_DELAY);
      };

      // Track mouse state on dropdown parent
      dropdown.addEventListener('mouseenter', function (e) {
        isMouseOverDropdown = true;
        showDropdown();
      });

      dropdown.addEventListener('mouseleave', function (e) {
        isMouseOverDropdown = false;
        // Check if mouse is moving to menu (relatedTarget)
        const relatedTarget = e.relatedTarget;
        if (relatedTarget && (menu.contains(relatedTarget) || menu === relatedTarget)) {
          // Mouse is moving to menu, don't hide
          return;
        }
        // Only start hide timer if not hovering over menu
        if (!isMouseOverMenu) {
          hideDropdown();
        }
      });

      // Track mouse state on menu itself
      menu.addEventListener('mouseenter', function (e) {
        isMouseOverMenu = true;
        showDropdown();
      });

      menu.addEventListener('mouseleave', function (e) {
        isMouseOverMenu = false;
        // Check if mouse is moving to dropdown (relatedTarget)
        const relatedTarget = e.relatedTarget;
        if (relatedTarget && (dropdown.contains(relatedTarget) || dropdown === relatedTarget)) {
          // Mouse is moving to dropdown, don't hide
          return;
        }
        // Only start hide timer if not hovering over dropdown
        if (!isMouseOverDropdown) {
          hideDropdown();
        }
      });

      // Also handle mouse movement within menu to keep it visible
      menu.addEventListener('mousemove', function () {
        if (!isMouseOverMenu) {
          isMouseOverMenu = true;
          showDropdown();
        }
      });
    });
  }

  /**
   * Setup dropdown position adjustment on hover and focus
   */
  function setupDropdownPositioning() {
    const dropdowns = document.querySelectorAll('.header-note__dropdown');

    dropdowns.forEach(function (dropdown) {
      const menu = dropdown.querySelector('.header-note__dropdown-menu');
      if (!menu) return;

      // Adjust position when dropdown becomes visible
      const adjustPosition = function () {
        // Use double requestAnimationFrame to ensure menu is visible and has dimensions
        // First frame: menu becomes visible
        requestAnimationFrame(function () {
          // Second frame: menu has dimensions, we can measure
          requestAnimationFrame(function () {
            adjustDropdownPosition(dropdown, menu);
          });
        });
      };

      // Handle mouseenter (hover) - adjust when menu appears
      dropdown.addEventListener('mouseenter', adjustPosition);

      // Also adjust when menu itself is hovered
      menu.addEventListener('mouseenter', adjustPosition);

      // Handle focus (keyboard navigation) - adjust when menu appears
      dropdown.addEventListener('focusin', adjustPosition);

      // Adjust on window resize
      let resizeTimeout;
      const resizeHandler = function () {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(function () {
          // Recalculate all dropdown positions on resize
          initializeDropdownPositions();
        }, 150);
      };

      window.addEventListener('resize', resizeHandler, { passive: true });

      // Also adjust on scroll (in case header is sticky/fixed)
      let scrollTimeout;
      const scrollHandler = function () {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function () {
          const isVisible = window.getComputedStyle(menu).visibility === 'visible' ||
            dropdown.matches(':hover') ||
            dropdown.matches(':focus-within');
          if (isVisible) {
            adjustPosition();
          }
        }, 50);
      };

      window.addEventListener('scroll', scrollHandler, { passive: true });
    });
  }

  /**
   * Handle dropdown menu keyboard navigation
   * Improves accessibility for dropdown menus
   */
  function setupDropdownKeyboardNav() {
    const dropdowns = document.querySelectorAll('.header-note__dropdown');

    dropdowns.forEach(function (dropdown) {
      const toggle = dropdown.querySelector('.header-note__nav-link');
      const menu = dropdown.querySelector('.header-note__dropdown-menu');

      if (!toggle || !menu) return;

      // Handle Escape key to close dropdown
      dropdown.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          toggle.focus();
          menu.style.opacity = '0';
          menu.style.visibility = 'hidden';
        }
      });

      // Handle Enter/Space on dropdown toggle
      toggle.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          const isExpanded = menu.style.visibility === 'visible';

          if (!isExpanded) {
            menu.style.opacity = '1';
            menu.style.visibility = 'visible';
            menu.style.transform = 'translateY(0)';

            // Adjust position after making visible
            setTimeout(function () {
              adjustDropdownPosition(dropdown, menu);
            }, 10);

            // Focus first link in dropdown
            const firstLink = menu.querySelector('.header-note__dropdown-link');
            if (firstLink) {
              firstLink.focus();
            }
          }
        }
      });
    });
  }

  /**
   * Handle mobile search visibility toggle
   * Allows toggling search on mobile devices
   */
  function setupMobileSearch() {
    const mobileSearch = document.querySelector('.header-note__mobile-search');
    const searchInput = mobileSearch?.querySelector('.header-note__search-input');

    if (searchInput) {
      // Auto-focus search input when mobile search becomes visible
      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && window.innerWidth < 700) {
            // Don't auto-focus on mobile to prevent keyboard popup
          }
        });
      });

      if (mobileSearch) {
        observer.observe(mobileSearch);
      }
    }
  }

  /**
   * Handle cart count updates and "has items" indicator
   * Listens for theme's cart:change event and toggles badge visibility + cart link state
   */
  function setupCartCountUpdates() {
    const cartLink = document.querySelector('.header-note__cart');
    const badge = document.querySelector('.header-note__cart-count');

    function updateCartIndicator(itemCount) {
      const count = typeof itemCount === 'number' ? itemCount : 0;
      const hasItems = count > 0;

      if (cartLink) {
        cartLink.classList.toggle('header-note__cart--has-items', hasItems);
      }
      if (badge) {
        badge.classList.toggle('header-note__cart-count--empty', !hasItems);
      }
    }

    // Theme dispatches cart:change with detail.cart.item_count
    document.addEventListener('cart:change', function (event) {
      const cart = event.detail && event.detail.cart;
      if (cart && typeof cart.item_count !== 'undefined') {
        updateCartIndicator(cart.item_count);
      }
    });

    // Initial state from DOM (Liquid sets --has-items / --empty on load)
    if (badge) {
      const cartCountEl = badge.querySelector('cart-count');
      const initial = cartCountEl ? parseInt(cartCountEl.textContent, 10) : 0;
      if (!isNaN(initial)) {
        updateCartIndicator(initial);
      }
    }
  }

  /**
   * Scroll-based navigation with collapse/expand toggle
   * - Scrolling down past threshold: hides mega menu, shows hamburger button
   * - Clicking hamburger: expands menu, switches to close icon
   * - Clicking close: collapses menu, switches to hamburger icon
   * - Scrolling back to top: resets everything to default state
   * - Anti-flicker: Uses hysteresis and distance threshold
   */
  function setupScrollNavigation() {
    // Check if we're on the homepage - don't apply scroll behavior there
    const isHomepage = document.body.classList.contains('template-index');
    if (isHomepage) {
      return;
    }

    const header = document.querySelector('.header-note');
    const scrollToggle = document.querySelector('.header-note__scroll-toggle');
    const navigation = document.querySelector('.header-note__nav');
    const logo = document.querySelector('.header-note__logo');

    if (!header || !scrollToggle || !navigation || !logo) {
      return;
    }

    // Configuration - Anti-flicker settings
    const THRESHOLD_ENTER = 100;          // Scroll down past this to enter "scrolled" mode
    const THRESHOLD_EXIT = 40;            // Scroll up past this to exit "scrolled" mode (hysteresis)
    const DISTANCE_THRESHOLD = 50;        // Accumulated scroll distance before state change
    const MIN_STATE_INTERVAL = 150;       // Minimum ms between state changes
    const DESKTOP_BREAKPOINT = 1024;
    const ANIMATION_DURATION = 400;

    // State
    let isInScrolledMode = false;         // Are we in scrolled mode? (showing toggle button)
    let isMenuExpanded = false;           // Is menu visible when in scrolled mode?
    let isAnimating = false;              // Prevent rapid clicks during animation
    let lastScrollY = 0;                  // Previous scroll position
    let scrollDirection = null;           // 'up' or 'down'
    let accumulatedDistance = 0;          // Accumulated scroll distance in current direction
    let lastStateChangeTime = 0;          // Timestamp of last state change
    let ticking = false;                  // RAF throttle flag

    /**
     * Apply the visual state to the DOM
     */
    function applyState() {
      if (isInScrolledMode) {
        // In scrolled mode - show toggle button
        header.classList.add('header-note--scrolled');
        logo.classList.add('logo-with-toggle');

        if (isMenuExpanded) {
          // Menu is visible - show close icon
          navigation.classList.remove('is-collapsed');
          navigation.classList.add('is-expanded');
          scrollToggle.classList.add('is-active');
          scrollToggle.setAttribute('aria-expanded', 'true');
        } else {
          // Menu is hidden - show hamburger icon
          navigation.classList.add('is-collapsed');
          navigation.classList.remove('is-expanded');
          scrollToggle.classList.remove('is-active');
          scrollToggle.setAttribute('aria-expanded', 'false');
        }
      } else {
        // Normal mode - show menu, hide toggle button
        header.classList.remove('header-note--scrolled');
        logo.classList.remove('logo-with-toggle');
        navigation.classList.remove('is-collapsed');
        navigation.classList.remove('is-expanded');
        scrollToggle.classList.remove('is-active');
        scrollToggle.setAttribute('aria-expanded', 'false');
      }
    }

    /**
     * Handle scroll events (throttled with RAF)
     */
    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(function () {
          processScroll();
          ticking = false;
        });
        ticking = true;
      }
    }

    /**
     * Process scroll with hysteresis and distance tracking
     */
    function processScroll() {
      // Only apply on desktop
      if (window.innerWidth < DESKTOP_BREAKPOINT) {
        return;
      }

      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
      const currentTime = Date.now();
      const scrollDelta = currentScrollY - lastScrollY;
      const newDirection = scrollDelta > 0 ? 'down' : scrollDelta < 0 ? 'up' : scrollDirection;

      // Track direction and accumulate distance
      if (newDirection !== scrollDirection) {
        // Direction changed - reset accumulated distance
        accumulatedDistance = 0;
        scrollDirection = newDirection;
      }
      accumulatedDistance += Math.abs(scrollDelta);

      // Check if we can change state (anti-flicker timing)
      const canChangeState = (currentTime - lastStateChangeTime) > MIN_STATE_INTERVAL;
      const hasEnoughDistance = accumulatedDistance > DISTANCE_THRESHOLD;

      // Determine if we should enter/exit scrolled mode
      const wasInScrolledMode = isInScrolledMode;

      if (!isInScrolledMode) {
        // Not in scrolled mode - check if we should enter
        // Enter when: scrolled past THRESHOLD_ENTER with enough downward distance
        if (currentScrollY > THRESHOLD_ENTER && canChangeState &&
          (hasEnoughDistance || currentScrollY > THRESHOLD_ENTER + 20)) {
          isInScrolledMode = true;
          isMenuExpanded = false; // Start with menu collapsed
          lastStateChangeTime = currentTime;
          accumulatedDistance = 0;
        }
      } else {
        // In scrolled mode - check if we should exit
        // Exit when: scrolled up past THRESHOLD_EXIT
        if (currentScrollY < THRESHOLD_EXIT && canChangeState) {
          isInScrolledMode = false;
          isMenuExpanded = false;
          lastStateChangeTime = currentTime;
          accumulatedDistance = 0;
        }
      }

      // Update last scroll position
      lastScrollY = currentScrollY;

      // Apply state if changed
      if (wasInScrolledMode !== isInScrolledMode) {
        applyState();
      }
    }

    /**
     * Handle toggle button click
     */
    function onToggleClick(e) {
      e.preventDefault();
      e.stopPropagation();

      // Prevent rapid clicking during animation or when not in scrolled mode
      if (isAnimating || !isInScrolledMode) {
        return;
      }

      // Toggle the menu state
      isMenuExpanded = !isMenuExpanded;
      isAnimating = true;

      applyState();

      // Allow new clicks after animation completes
      setTimeout(function () {
        isAnimating = false;
      }, ANIMATION_DURATION);
    }

    /**
     * Handle window resize
     */
    function onResize() {
      // Reset everything on mobile
      if (window.innerWidth < DESKTOP_BREAKPOINT) {
        isInScrolledMode = false;
        isMenuExpanded = false;
        isAnimating = false;
        ticking = false;
        accumulatedDistance = 0;
        scrollDirection = null;
        header.classList.remove('header-note--scrolled');
        navigation.classList.remove('is-collapsed', 'is-expanded');
        scrollToggle.classList.remove('is-active');
        logo.classList.remove('logo-with-toggle');
      } else {
        // Re-evaluate on desktop
        processScroll();
      }
    }

    /**
     * Initialize with current scroll position
     * Works even if page loads already scrolled
     */
    function initializeState() {
      // Only on desktop
      if (window.innerWidth < DESKTOP_BREAKPOINT) {
        return;
      }

      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
      lastScrollY = currentScrollY;

      // If already scrolled past threshold on page load, immediately enter scrolled mode
      if (currentScrollY > THRESHOLD_ENTER) {
        isInScrolledMode = true;
        isMenuExpanded = false;
        lastStateChangeTime = Date.now();
        applyState();
      }
    }

    // Set up event listeners
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    scrollToggle.addEventListener('click', onToggleClick);

    // Initialize state immediately (handles page load at any scroll position)
    initializeState();
  }

  /**
   * Handle mobile menu toggle icon state
   * Updates aria-expanded when sidebar opens/closes
   */
  function setupMobileMenuToggle() {
    const mobileToggle = document.querySelector('.header-note__mobile-toggle');
    const sidebar = document.getElementById('sidebar-menu');

    if (!mobileToggle || !sidebar) return;

    // Update toggle state when sidebar opens/closes
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const isOpen = sidebar.hasAttribute('open');
          mobileToggle.setAttribute('aria-expanded', isOpen);
        }
      });
    });

    // Observe sidebar for open attribute changes
    observer.observe(sidebar, {
      attributes: true,
      attributeFilter: ['open']
    });

    // Set initial state
    const initiallyOpen = sidebar.hasAttribute('open');
    mobileToggle.setAttribute('aria-expanded', initiallyOpen);
  }

  /**
   * Initialize all header functionality
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        initHeaderHeight();
        setupResizeHandler();
        // Initialize dropdown positions first to prevent scrollbar
        initializeDropdownPositions();
        setupDropdownHoverDelay();
        setupDropdownPositioning();
        setupDropdownKeyboardNav();
        setupMobileSearch();
        setupCartCountUpdates();
        setupScrollNavigation();
        setupMobileMenuToggle();
      });
    } else {
      initHeaderHeight();
      setupResizeHandler();
      // Initialize dropdown positions first to prevent scrollbar
      initializeDropdownPositions();
      setupDropdownHoverDelay();
      setupDropdownPositioning();
      setupDropdownKeyboardNav();
      setupMobileSearch();
      setupCartCountUpdates();
      setupScrollNavigation();
      setupMobileMenuToggle();
    }
  }

  // Initialize
  init();

})();
